import { DOCUMENT } from '@angular/common';
import { Component, HostListener, Inject, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';

import { LanguageService } from './core/services';
import { SignInComponent } from "./sign-in/sign-in";
import { RouterOutlet } from '@angular/router';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [FormsModule, RouterOutlet],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css']
})
export class AppComponent {
  private readonly languageSubscription: Subscription;

  constructor(
    private readonly language: LanguageService,
    @Inject(DOCUMENT) private readonly document: Document
  ) {
    this.applyLanguage(this.language.currentLanguage);
    this.languageSubscription = this.language.language$.subscribe((currentLanguage) => {
      this.applyLanguage(currentLanguage);
    });
  }

  ngOnDestroy(): void {
    this.languageSubscription.unsubscribe();
  }

  @HostListener('document:beforeinput', ['$event'])
  onDocumentBeforeInput(event: InputEvent): void {
    const target = this.resolveTextInputTarget(event.target);
    if (!target) {
      return;
    }

    if (event.inputType?.startsWith('delete')) {
      return;
    }

    const incoming = `${event.data ?? ''}`;
    if (!incoming) {
      return;
    }

    if (!this.isAsciiText(incoming)) {
      event.preventDefault();
    }
  }

  @HostListener('document:input', ['$event'])
  onDocumentInput(event: Event): void {
    const target = this.resolveTextInputTarget(event.target);
    if (!target) {
      return;
    }

    const sanitized = this.sanitizeAscii(target.value);
    if (sanitized === target.value) {
      return;
    }

    const cursorStart = target.selectionStart ?? sanitized.length;
    const removedCount = target.value.length - sanitized.length;
    target.value = sanitized;
    const nextCursor = Math.max(0, cursorStart - Math.max(removedCount, 0));
    target.setSelectionRange(nextCursor, nextCursor);
    target.dispatchEvent(new Event('input', { bubbles: true }));
  }

  @HostListener('document:paste', ['$event'])
  onDocumentPaste(event: ClipboardEvent): void {
    const target = this.resolveTextInputTarget(event.target);
    if (!target) {
      return;
    }

    const pasted = `${event.clipboardData?.getData('text') ?? ''}`;
    if (!pasted) {
      return;
    }

    if (!this.isAsciiText(pasted)) {
      event.preventDefault();
    }
  }

  private applyLanguage(language: 'bn' | 'en'): void {
    this.document.documentElement.lang = language;
    this.document.body.classList.toggle('lang-bn', language === 'bn');
    this.document.body.classList.toggle('lang-en', language === 'en');
  }

  private resolveTextInputTarget(target: EventTarget | null): HTMLInputElement | HTMLTextAreaElement | null {
    if (target instanceof HTMLInputElement) {
      const supportedTypes = new Set(['', 'text', 'email', 'password', 'search', 'tel', 'url']);
      return supportedTypes.has((target.type ?? '').toLowerCase()) ? target : null;
    }

    if (target instanceof HTMLTextAreaElement) {
      return target;
    }

    return null;
  }

  private isAsciiText(value: string): boolean {
    return this.sanitizeAscii(value) === value;
  }

  private sanitizeAscii(value: string): string {
    return `${value ?? ''}`.replace(/[^\x20-\x7E\r\n\t]/g, '');
  }
}
